import { Axial, axialToWorld } from './HexCoordinates';

/**
 * The chunk lattice: which chunk a cell belongs to, and where a chunk sits.
 *
 * Twin of `crates/world-gen/src/grid.rs`. The two must agree exactly — the
 * lattice decides which bytes of a world file a chunk is, so a disagreement
 * would not look like a bug, it would look like terrain from somewhere else.
 * The Rust side has the tests; this side has the same maths and this comment.
 *
 * Chunks tile the plane on the vector `(2N+1, −N)` and its 60° rotation, where
 * N is the chunk radius in cells. That pair is what makes radius-N hexagons
 * cover the plane with no gaps and no overlaps.
 */

/** Cube distance from the origin — the number of steps to walk there. */
export function hexDistance(a: Axial): number {
  return Math.max(Math.abs(a.q), Math.abs(a.r), Math.abs(a.q + a.r));
}

/** The tiling basis for a given chunk radius. */
export function latticeBasis(chunkRadius: number): [Axial, Axial] {
  const v1: Axial = { q: 2 * chunkRadius + 1, r: -chunkRadius };
  // 60° rotation, via the cube rotation (x, y, z) -> (-z, -x, -y)
  const v2: Axial = { q: -v1.r, r: v1.q + v1.r };
  return [v1, v2];
}

/** World cell at the centre of a chunk, from its lattice coordinates. */
export function chunkCenter(lattice: Axial, chunkRadius: number): Axial {
  const [v1, v2] = latticeBasis(chunkRadius);
  return {
    q: lattice.q * v1.q + lattice.r * v2.q,
    r: lattice.q * v1.r + lattice.r * v2.r,
  };
}

/**
 * Which chunk holds a cell.
 *
 * Solving the 2×2 system gives a real-valued answer, and rounding it is not
 * enough: the hexagon and the parallelogram spanned by the basis disagree near
 * the corners, the same way axial rounding does for cells. So the nine
 * candidates around the rounded answer are tried and the one that actually
 * contains the cell wins — one of them always does, because the lattice tiles
 * the plane.
 */
export function chunkOf(cell: Axial, chunkRadius: number): Axial {
  const [v1, v2] = latticeBasis(chunkRadius);

  const det = v1.q * v2.r - v2.q * v1.r;
  const a0 = Math.round((cell.q * v2.r - cell.r * v2.q) / det);
  const b0 = Math.round((v1.q * cell.r - v1.r * cell.q) / det);

  for (let da = -1; da <= 1; da++) {
    for (let db = -1; db <= 1; db++) {
      const lattice = { q: a0 + da, r: b0 + db };
      const center = chunkCenter(lattice, chunkRadius);
      const offset = { q: cell.q - center.q, r: cell.r - center.r };
      if (hexDistance(offset) <= chunkRadius) return lattice;
    }
  }

  return { q: a0, r: b0 };
}

/** Lattice coordinates within `radius` rings of `center`, nearest first. */
export function latticeArea(center: Axial, radius: number): Axial[] {
  const found: { lattice: Axial; distance: number }[] = [];

  for (let q = -radius; q <= radius; q++) {
    const from = Math.max(-radius, -q - radius);
    const to = Math.min(radius, -q + radius);
    for (let r = from; r <= to; r++) {
      found.push({
        lattice: { q: center.q + q, r: center.r + r },
        distance: hexDistance({ q, r }),
      });
    }
  }

  found.sort((a, b) => a.distance - b.distance);
  return found.map((entry) => entry.lattice);
}

/** Is this chunk part of a world of the given lattice radius? */
export function chunkExists(lattice: Axial, latticeRadius: number): boolean {
  return hexDistance(lattice) <= latticeRadius;
}

/**
 * World-space distance between the centres of two neighbouring chunks.
 *
 * Used to turn "how much ground is on screen" into "how many rings of chunks",
 * so the load radius follows the zoom instead of being a guess that is wrong at
 * one end of the range.
 */
export function chunkSpacing(chunkRadius: number): number {
  const [v1] = latticeBasis(chunkRadius);
  const { x, z } = axialToWorld(v1.q, v1.r);
  return Math.hypot(x, z);
}
